import type {ChangePlanArgs, ChangePlanCost, ChangePlanCostArgs, ChangePlanModalState, PromoEligiblePlans, Tier, ValidatePromoCodeArgs} from '@Panels/change-plan-modal/change-plan-modal.types';
import type {AddTeamMembersDialogData} from '@Panels/add-team-members-panel/add-team-members-panel.types';
import type {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import {PlanDurations} from '@Libraries/change-subscription-plan-library';

export type {
  AppleSubscriptionResponse,
  ChangePlanArgs,
  ChangePlanCostArgs,
  ChangePlanSuccessResponse,
  IAPChangePlanState,
  IAPProduct,
  PlanFilter,
  PromoEligiblePlans,
  SubscriptionPlan,
  Tier,
  ValidatePromoCodeArgs,
  ValidatePromoCodeResponse,
} from '@Panels/change-plan-modal/change-plan-modal.types';

export const CHANGE_PLAN_MODAL_V2_PANEL_ID = 'change-plan-modal-v2-panel';
export const CHANGE_PLAN_MODAL_V2_TWO_COLUMN_WIDTH = '1000px';
export const CHANGE_PLAN_MODAL_V2_SINGLE_COLUMN_WIDTH = '581px';
export const CHANGE_PLAN_MODAL_V2_HEIGHT = '629px';
export const CHANGE_PLAN_MODAL_V2_RIGHT_COLUMN_BREAKPOINT = 900;
export const CHANGE_PLAN_MODAL_V2_MOBILE_BREAKPOINT = 780;

/**
 * The design system turns any modal into a full-width sheet below 781px, which would make tablet a sheet; the
 * hand-off draws tablet as a centred dialog. 629 is the narrowest viewport where the 581px dialog still clears
 * the modal's own 48px margin, so a sheet is only right below that. Scoped to this dialog, since the shared
 * default is used by every other modal in the app.
 */
export const CHANGE_PLAN_MODAL_V2_SHEET_BREAKPOINT = 629;

/**
 * Quarterly is grandfathered: a subscriber already on it keeps it until they move, but no one can change on to it, so
 * the billing-cycle toggle only ever offers these two.
 */
export const SELECTABLE_PLAN_DURATIONS: number[] = [PlanDurations.MONTHLY.valueOf(), PlanDurations.YEARLY.valueOf()];

/** Maps a subscriber's own duration onto one the dialog can offer, so a quarterly subscriber opens on monthly. */
export function getSelectableDuration(duration: number): number {
  return SELECTABLE_PLAN_DURATIONS.includes(duration) ? duration : PlanDurations.MONTHLY.valueOf();
}

export enum ChangePlanStepV2 {
  CHANGE_PLAN = 'change-plan',
  REMOVE_TEAM_MEMBERS = 'remove-team-members',
  TEAM = 'team',
  SUMMARY = 'summary',
}

/** Steps without a right column, which shrink the dialog to a single column. */
export const SINGLE_COLUMN_STEPS: ChangePlanStepV2[] = [ChangePlanStepV2.REMOVE_TEAM_MEMBERS, ChangePlanStepV2.SUMMARY];

export enum TeamStepVariant {
  /** Reached from the plan step after adding seats: the seats are chosen, only the invites remain. */
  INVITE = 'invite',
  /** Entry point of the billing page's "Add seats": seats and invites are chosen together on the current plan. */
  GROW_WORKSPACE = 'grow-workspace',
}

export interface ChangePlanModalOpenOptions {
  /** A promo code to validate and apply for the user as soon as the preselected plan is loaded. */
  prefillPromoCode?: string;
  /** Opens the dialog on the team step; the classic dialog ignores it. Only this step is wired for direct entry. */
  initialStep?: ChangePlanStepV2.TEAM;
}

/** What a caller can preselect when opening the dialog, as one argument so no position has to be padded with `undefined`. */
export interface ChangePlanModalOpenRequest extends ChangePlanModalOpenOptions {
  preselectLevel?: number;
  preselectDuration?: number;
}

export interface ChangePlanModalV2Props extends ChangePlanModalOpenOptions {
  panelId: string;
  modalId: string;
  preselectLevel?: number;
  preselectDuration?: number;
  /** Opened from the manageapp design-system preview: reads stand-in data and never charges. */
  isPreview?: boolean;
}

export interface CurrentPlanSelectionCheck {
  currentLevel: number;
  currentDuration: number;
  currentTierId: number | null;
  currentSeatLimit: number;
  selectedLevel: number;
  selectedDuration: number;
  selectedTier: Tier | null;
  selectedSeats: number;
}

/** The live quote and the design-system preview's stand-in quote take the same selection. */
export interface ChangePlanCostV2Args extends ChangePlanCostArgs {
  isPreview: boolean;
  /** 1 once the carried code is dropped, so the preview prices the change without it. */
  isPromoRemoved: number;
}

export interface ChangePlanV2Args extends ChangePlanArgs {
  /** 1 once the carried code is dropped, so the backend detaches it before applying whatever replaced it. */
  isPromoRemoved: number;
}

export interface ValidatePromoCodeV2Args extends ValidatePromoCodeArgs {
  /** 1 once the carried code is dropped, so a replacement is not rejected for stacking on its way out. */
  isPromoRemoved: number;
}

export interface ChangePlanCostV2 extends ChangePlanCost {
  /** Unix timestamp of the next charge after the change lands; 0 when the backend could not tell. */
  nextBillingDate: number;
  /** Days left in the current cycle that the proration covers; 0 when the change is not prorated. */
  remainingDaysInCycle: number;
}

export interface PlanLimits {
  premiumMonthlyCredits: number;
  premiumPlusMonthlyCredits: number;
  premiumMonthlyEmailSends: number;
  premiumPlusMonthlyEmailSends: number;
  premiumSocialAccounts: number;
  premiumEventPagesPerMonth: number;
  premiumTicketsPerMonth: number;
  premiumTicketRevenuePerMonth: number;
  premiumTicketServiceFeePercent: number;
  premiumPlusTicketServiceFeePercent: number;
}

export type TeamInviteContext = Pick<AddTeamMembersDialogData, 'currentUser' | 'filledSeats' | 'inviteLink' | 'suggestedTeammates'>;

export interface ChangePlanDialogV2Response extends ChangePlanModalState {
  isTicketingAvailableInUserCountry: boolean;
  planLimits: PlanLimits;
  /** The plan levels and durations the carried code discounts; empty when the subscription carries no code. */
  promoEligiblePlans: PromoEligiblePlans;
}

export interface ChangePlanModalV2State extends ChangePlanDialogV2Response {
  /** The code the subscription arrived with, kept so dropping it can be given back when the quote is discarded. */
  carriedPromoCode: string;
  /** Set when the subscriber drops the code their subscription carries, so the change is priced and billed without it. */
  isSubscriptionPromoRemoved: boolean;
  isFetchingTeamMembers: boolean;
  currentStep: ChangePlanStepV2;
  stepHistory: ChangePlanStepV2[];
  transitionDirection: TransitionDirection;
  teamStepVariant: TeamStepVariant;
  changePlanCost: ChangePlanCostV2;
  inviteEmails: string[];
  teamInviteContext: TeamInviteContext | null;
}

export const DEFAULT_CHANGE_PLAN_COST_V2: ChangePlanCostV2 = {
  amountDue: 0,
  discount: 0,
  taxAmount: 0,
  balance: 0,
  endingBalance: 0,
  unusedTimeAmount: 0,
  remainingTimeAmount: 0,
  encryptedTime: '',
  nextBillingDate: 0,
  remainingDaysInCycle: 0,
};
